import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';

const languages = [
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'kk', label: 'Қазақша', flag: '🇰🇿' },
  { code: 'az', label: 'Azərbaycanca', flag: '🇦🇿' },
];

const Wrapper = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  color: #fff;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 160px;
  background: #fff;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  box-shadow: 0 8px 32px rgba(0, 66, 66, 0.15);
  overflow: hidden;
  z-index: 100;
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: ${({ $active }) => ($active ? 'rgba(2, 145, 239, 0.06)' : 'transparent')};
  border: none;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(2, 145, 239, 0.08);
  }
`;

const Flag = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const Label = styled.span`
  flex: 1;
  text-align: left;
`;

const CheckIcon = styled.span`
  color: ${({ theme }) => theme.colors.primary.main};
  display: flex;
`;

export default function LanguagePicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <Wrapper ref={ref}>
      <Trigger type="button" onClick={() => setOpen(!open)}>
        <Globe size={16} />
        {current.flag} {current.label}
      </Trigger>
      {open && (
        <Dropdown>
          {languages.map((lang) => (
            <Option
              key={lang.code}
              $active={lang.code === i18n.language}
              onClick={() => handleSelect(lang.code)}
            >
              <Flag>{lang.flag}</Flag>
              <Label>{lang.label}</Label>
              {lang.code === i18n.language && (
                <CheckIcon><Check size={16} /></CheckIcon>
              )}
            </Option>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  );
}
